import * as React from 'react';
import {
  View,
  StyleSheet,
  Keyboard,
  LayoutAnimation,
  Platform,
  type ViewProps,
} from 'react-native';

type Props = ViewProps & {
  children: React.ReactNode;
  /** Keep parity with your iOS version */
  insideTab?: boolean;
  /** Extra bottom offset to subtract from IME height (status/tab/header, etc.) */
  keyboardVerticalOffset?: number;
  /** Enable/disable behavior */
  enabled?: boolean;
  /** Animate spacer changes */
  animate?: boolean;
};

const TAB_BAR_HEIGHT = 49;

type State = {height: number};

const isAndroid15Plus =
  Platform.OS === 'android' && Number(Platform.Version) >= 35;

export default class KeyboardAvoidingViewAndroid extends React.PureComponent<
  Props,
  State
> {
  static defaultProps = {
    enabled: true,
    insideTab: false,
    keyboardVerticalOffset: 0,
    animate: true,
  };

  state: State = {height: 0};
  private subs: Array<{remove(): void}> = [];

  componentDidMount() {
    if (!this.props.enabled || Platform.OS !== 'android') {
      return;
    }

    // These cover show/hide and height changes (gesture keyboard, split modes, etc.)
    this.subs = [
      Keyboard.addListener('keyboardDidShow', this.onShowOrChange),
      Keyboard.addListener('keyboardDidChangeFrame', this.onShowOrChange),
      Keyboard.addListener('keyboardDidHide', this.onHide),
    ];
  }

  componentWillUnmount() {
    this.subs.forEach((s) => s.remove());
    this.subs = [];
  }

  private animateIfNeeded() {
    if (!this.props.animate) {
      return;
    }
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
  }

  private onShowOrChange = (e) => {
    const raw = e?.endCoordinates?.height ?? 0;

    // Subtract any known UI you don’t want to push above
    const extra =
      (this.props.insideTab ? TAB_BAR_HEIGHT : 0) +
      (this.props.keyboardVerticalOffset || 0);

    const next = Math.max(0, raw - extra);

    if (next !== this.state.height) {
      this.animateIfNeeded();
      this.setState({height: next});
    }
  };

  private onHide = () => {
    if (this.state.height !== 0) {
      this.animateIfNeeded();
      this.setState({height: 0});
    }
  };

  render() {
    const {style, children, ...rest} = this.props;

    // Strip custom props so they don't hit <View>
    delete rest.insideTab;
    delete rest.keyboardVerticalOffset;
    delete rest.enabled;
    delete rest.animate;

    if (!isAndroid15Plus) {
      return (
        <View style={[styles.flex, style]} {...rest}>
          {children}
        </View>
      );
    }

    return (
      <View style={styles.flex} {...rest}>
        <View style={[styles.flex, style]}>{children}</View>
        <View style={{height: this.state.height}} />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  flex: {flex: 1},
});
